import { init, loadRemote, registerRemotes } from '@module-federation/runtime';
import { validateRemoteNavigation, validateRemoteResources } from '@unified-governance/shared';
import type {
  MountAuth,
  MountFn,
  NavNode,
  RemoteNavigation,
  RemoteRegistry,
  RemoteResources,
} from '@unified-governance/shared';

export type RemoteNavigationWithHelp = RemoteNavigation & {
  itemIconDataUrls?: Record<string, string>;
  groupIconDataUrls?: Record<string, string>;
};

type ResolveNavigationFn = (auth: MountAuth) => Promise<unknown>;

const ENABLE_NAVIGATION_FILTERING = true;
const ALLOWED_NAVIGATION_LABELS = new Set(['apps & agents', 'requests']);
const REMOTE_LOAD_TIMEOUT_MS = 15_000;

let initialized = false;

function withRemoteTimeout<T>(promise: Promise<T>, remoteName: string, moduleName: string): Promise<T> {
  return new Promise<T>((resolve, reject) => {
    const timeoutId = window.setTimeout(() => {
      reject(
        new Error(
          `Remote ${remoteName}/${moduleName} did not load within ${REMOTE_LOAD_TIMEOUT_MS}ms`
        )
      );
    }, REMOTE_LOAD_TIMEOUT_MS);

    promise.then(
      (value) => {
        window.clearTimeout(timeoutId);
        resolve(value);
      },
      (error: unknown) => {
        window.clearTimeout(timeoutId);
        reject(error);
      }
    );
  });
}

function isAllowedNavigationLabel(label: string): boolean {
  return ALLOWED_NAVIGATION_LABELS.has(label.trim().toLowerCase());
}

function filterAllowedNavigationItems(items: NavNode[]): NavNode[] {
  const allowedItems: NavNode[] = [];

  for (const item of items) {
    if (item.kind === 'item' && isAllowedNavigationLabel(item.label)) {
      allowedItems.push(item);
      continue;
    }

    if (item.kind === 'group') {
      const children = item.children.filter((child) => isAllowedNavigationLabel(child.label));
      if (children.length > 0) {
        allowedItems.push({ ...item, children });
      }
    }
  }

  return allowedItems;
}

export function initFederation(): void {
  if (initialized) {
    return;
  }
  init({ name: 'unified_governance_shell', remotes: [] });
  initialized = true;
}

export function registerRemoteEntries(registry: RemoteRegistry): void {
  registerRemotes(
    registry.map((entry) => ({
      name: entry.name,
      entry: entry.url,
      type: 'module' as const,
    }))
  );
}

export async function loadRemoteNavigation(
  name: string,
  auth?: MountAuth
): Promise<RemoteNavigationWithHelp | null> {
  try {
    const loaded = await withRemoteTimeout(
      loadRemote<{
        navigation?: unknown;
        resolveNavigation?: ResolveNavigationFn;
      }>(`${name}/navigation`),
      name,
      'navigation'
    );
    if (!loaded) {
      console.warn('[shell] Remote navigation module was not loaded', name);
      return null;
    }
    let navigation = loaded.navigation;
    if (auth && typeof loaded.resolveNavigation === 'function') {
      try {
        navigation = await withRemoteTimeout(
          loaded.resolveNavigation(auth),
          name,
          'navigation resolver'
        );
      } catch (error) {
        console.warn('[shell] Remote navigation resolver failed', name, error);
        return null;
      }
    }
    const result = validateRemoteNavigation(navigation);
    if (!result.ok) {
      console.warn('[shell] Invalid remote navigation', name, result.errors);
    }
    if (!result.ok || !result.value) {
      return null;
    }

    const itemIconDataUrls = validateItemIconDataUrls(navigation);
    if (itemIconDataUrls === null) {
      console.warn('[shell] Invalid remote navigation icon data URLs', name);
      return null;
    }

    const groupIconDataUrls = validateGroupIconDataUrls(navigation);
    if (groupIconDataUrls === null) {
      console.warn('[shell] Invalid remote navigation group icon data URLs', name);
      return null;
    }

    const items = ENABLE_NAVIGATION_FILTERING
      ? filterAllowedNavigationItems(result.value.items)
      : result.value.items;

    return {
      ...result.value,
      items,
      itemIconDataUrls,
      groupIconDataUrls,
    };
  } catch (error) {
    console.warn('[shell] Remote navigation could not be loaded', name, error);
    return null;
  }
}

const MAX_ICON_DATA_URL_LENGTH = 16 * 1024;
const MAX_ICON_DATA_URLS_TOTAL_LENGTH = 64 * 1024;
const SVG_DATA_URL_PREFIX = /^data:image\/svg\+xml(?:;charset=[^;,]+)?(?:;base64)?,/i;

function createIconDataUrlMap(): Record<string, string> {
  return Object.create(null) as Record<string, string>;
}

function validateItemIconDataUrls(navigation: unknown): Record<string, string> | null {
  if (typeof navigation !== 'object' || navigation === null) {
    return null;
  }

  const value = (navigation as Record<string, unknown>).itemIconDataUrls;
  if (value === undefined) {
    return createIconDataUrlMap();
  }
  if (typeof value !== 'object' || value === null || Array.isArray(value)) {
    return null;
  }

  const icons = createIconDataUrlMap();
  let totalLength = 0;
  for (const [path, dataUrl] of Object.entries(value)) {
    if (
      !path.startsWith('/') ||
      typeof dataUrl !== 'string' ||
      !SVG_DATA_URL_PREFIX.test(dataUrl) ||
      dataUrl.length > MAX_ICON_DATA_URL_LENGTH
    ) {
      return null;
    }
    totalLength += dataUrl.length;
    icons[path] = dataUrl;
  }

  return totalLength <= MAX_ICON_DATA_URLS_TOTAL_LENGTH ? icons : null;
}

function validateGroupIconDataUrls(navigation: unknown): Record<string, string> | null {
  if (typeof navigation !== 'object' || navigation === null) {
    return null;
  }

  const value = (navigation as Record<string, unknown>).groupIconDataUrls;
  if (value === undefined) {
    return createIconDataUrlMap();
  }
  if (typeof value !== 'object' || value === null || Array.isArray(value)) {
    return null;
  }

  const icons = createIconDataUrlMap();
  let totalLength = 0;
  for (const [groupId, dataUrl] of Object.entries(value)) {
    if (
      groupId.length === 0 ||
      typeof dataUrl !== 'string' ||
      !SVG_DATA_URL_PREFIX.test(dataUrl) ||
      dataUrl.length > MAX_ICON_DATA_URL_LENGTH
    ) {
      return null;
    }
    totalLength += dataUrl.length;
    icons[groupId] = dataUrl;
  }

  return totalLength <= MAX_ICON_DATA_URLS_TOTAL_LENGTH ? icons : null;
}

export async function loadRemoteMount(name: string): Promise<MountFn | null> {
  const loaded = await withRemoteTimeout(
    loadRemote<{ mount?: MountFn; default?: MountFn }>(`${name}/mount`),
    name,
    'mount'
  );
  if (!loaded) {
    return null;
  }
  return loaded.mount ?? loaded.default ?? null;
}

export async function loadRemoteResources(name: string): Promise<RemoteResources | null> {
  try {
    const loaded = await withRemoteTimeout(
      loadRemote<{
        resources?: unknown;
        default?: unknown;
      }>(`${name}/resources`),
      name,
      'resources'
    );

    if (!loaded) {
      console.warn('[shell] Remote resources module was not loaded', name);
      return null;
    }

    const resources = loaded.resources ?? loaded.default;
    const result = validateRemoteResources(resources);

    if (!result.ok) {
      console.warn('[shell] Invalid remote resources', name, result.errors);
      return null;
    }

    return result.value ?? null;
  } catch (error) {
    console.warn('[shell] Remote resources could not be loaded', name, error);
    return null;
  }
}
